export type EventType =
  | 'session_ready'
  | 'session_expired'
  | 'purchase_initiated'
  | 'purchase_completed'
  | 'purchase_failed'
  | 'reveal_started'
  | 'item_revealed'
  | 'item_sold'
  | 'reveal_complete'
  | 'balance_updated'
  | 'shipping_failed'
  | 'navigate_back_result'
  | 'navigate_lobby'
  | 'navigate_deposit'
  | 'haptic'
  | 'error';

export interface SessionReadyPayload {
  user_id: number;
  balance_cents: number;
}

export interface PurchaseInitiatedPayload {
  order_id: string;
}

export interface PurchaseCompletedPayload {
  order_id: string;
  items: Array<{ id: string; name: string; rarity: string }>;
}

export interface PurchaseFailedPayload {
  order_id: string;
  code?: string;
  message: string;
}

export interface RevealStartedPayload {
  order_id: string;
  item_count: number;
}

export interface ItemRevealedPayload {
  order_id: string;
  item_id: string;
  name: string;
  fair_market_value: number | null;
}

export interface ItemSoldPayload {
  order_id: string;
  item_id: string;
  gross_price: number;
}

export interface RevealCompletePayload {
  order_id: string;
}

export interface BalanceUpdatedPayload {
  balance_cents: number;
  currency: string;
}

/** Sent only when the operator hid our low balance message (LowBalance knob):
 *  the delivery fee couldn't be charged and the host shows its own top-up. */
export interface ShippingFailedPayload {
  code: string;
  message: string;
}

export interface NavigateBackResultPayload {
  /** false = the iframe is at its root; the host runs its own back. */
  handled: boolean;
}

/** Platform-neutral strength, so a native host maps it straight onto its own
 *  haptics API (iOS impact / notification feedback, Android feedback constants). */
export type HapticIntent = 'light' | 'medium' | 'heavy' | 'success';

export interface HapticPayload {
  intent: HapticIntent;
}

export interface ErrorPayload {
  message: string;
  stack?: string;
}

export interface GamePlatformEvent<T extends EventType = EventType> {
  source: 'repackz';
  type: T;
  payload?: unknown;
}

export interface EventPayloadMap {
  session_ready: SessionReadyPayload;
  session_expired: undefined;
  purchase_initiated: PurchaseInitiatedPayload;
  purchase_completed: PurchaseCompletedPayload;
  purchase_failed: PurchaseFailedPayload;
  reveal_started: RevealStartedPayload;
  item_revealed: ItemRevealedPayload;
  item_sold: ItemSoldPayload;
  reveal_complete: RevealCompletePayload;
  balance_updated: BalanceUpdatedPayload;
  shipping_failed: ShippingFailedPayload;
  navigate_back_result: NavigateBackResultPayload;
  navigate_lobby: undefined;
  navigate_deposit: undefined;
  haptic: HapticPayload;
  error: ErrorPayload;
}

// ──────────────────────────────────────────────────────────────────────────
// Incoming events (parent platform → iframe). Envelope uses a different source
// so our own outgoing events don't echo back when someone listens to
// window.message indiscriminately.

export type IncomingEventType =
  | 'balance_changed'
  | 'close_request'
  | 'navigate_back'
  | 'navigate_lobby_result'
  | 'navigate_deposit_result'
  | 'enable_haptics';

export interface IncomingEvent<T extends IncomingEventType = IncomingEventType> {
  source: 'repackz-parent';
  type: T;
  payload?: unknown;
}

export function isIncomingEvent(data: unknown): data is IncomingEvent {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { source?: unknown }).source === 'repackz-parent' &&
    typeof (data as { type?: unknown }).type === 'string'
  );
}
